import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  .bl-root {
    font-family: 'DM Sans', sans-serif;
    background: #FAFAF8;
    min-height: 100vh;
    color: #1a1a18;
  }

  .bl-hero {
    background: #0f2318;
    padding: 80px 24px 64px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .bl-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 60% 0%, rgba(29,158,117,0.18) 0%, transparent 65%),
                radial-gradient(ellipse at 20% 100%, rgba(29,158,117,0.10) 0%, transparent 60%);
  }

  .bl-tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #5DCAA5;
    background: rgba(93,202,165,0.12);
    border: 1px solid rgba(93,202,165,0.25);
    border-radius: 100px;
    padding: 5px 14px;
    margin-bottom: 24px;
    position: relative;
  }

  .bl-hero-title {
    font-family: 'Lora', Georgia, serif;
    font-size: clamp(28px, 5vw, 48px);
    font-weight: 600;
    color: #f5f3ee;
    max-width: 600px;
    margin: 0 auto 16px;
    line-height: 1.2;
    position: relative;
  }

  .bl-hero-sub {
    font-size: 16px;
    color: rgba(245,243,238,0.5);
    position: relative;
    font-weight: 300;
    max-width: 440px;
    margin: 0 auto;
  }

  .bl-body {
    max-width: 760px;
    margin: 0 auto;
    padding: 64px 24px 96px;
  }

  .bl-grid {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .bl-card {
    background: #fff;
    border: 1px solid #e8e6e0;
    border-radius: 16px;
    padding: 32px;
    text-decoration: none;
    color: inherit;
    display: block;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  }

  .bl-card:hover {
    border-color: rgba(29,158,117,0.4);
    box-shadow: 0 8px 32px rgba(29,158,117,0.08);
    transform: translateY(-2px);
  }

  .bl-card-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  .bl-card-tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #1D9E75;
    background: rgba(29,158,117,0.08);
    border: 1px solid rgba(29,158,117,0.2);
    border-radius: 100px;
    padding: 3px 10px;
  }

  .bl-card-date {
    font-size: 12px;
    color: #9a9a96;
    font-weight: 300;
  }

  .bl-card-read {
    font-size: 12px;
    color: #9a9a96;
    font-weight: 300;
  }

  .bl-card-title {
    font-family: 'Lora', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
    color: #0f2318;
    margin: 0 0 10px;
    line-height: 1.3;
  }

  .bl-card-desc {
    font-size: 15px;
    line-height: 1.7;
    color: #5a5a56;
    font-weight: 300;
    margin: 0 0 20px;
  }

  .bl-card-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #1D9E75;
  }

  .bl-card-link svg {
    transition: transform 0.2s;
  }

  .bl-card:hover .bl-card-link svg {
    transform: translateX(4px);
  }

  .bl-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 48px;
    padding-bottom: 32px;
    border-bottom: 1px solid #e8e6e0;
  }

  .bl-nav-home {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    color: #5a5a56;
    transition: color 0.2s;
  }

  .bl-nav-home:hover {
    color: #1D9E75;
  }

  .bl-count {
    font-size: 13px;
    color: #9a9a96;
  }
`;

const posts = [
  {
    slug: "/blog/how-to-add-chatbot-to-website",
    tag: "Beginner Guide",
    date: "May 2026",
    read: "6 min read",
    title: "How to Add a Chatbot to Your Website (Step-by-Step Guide)",
    desc: "Adding a chatbot to your website used to require a developer and weeks of setup. Not anymore. This guide shows you exactly how to do it in under 10 minutes — no coding required.",
  },
  {
    slug: "/blog/ai-chatbot-small-business",
    tag: "AI Strategy",
    date: "May 2026",
    read: "8 min read",
    title: "Why Every Small Business Website Needs an AI Chatbot in 2026",
    desc: "Customers expect instant answers 24/7. Calls go to voicemail. Emails sit unanswered. Here's why an AI chatbot is the most impactful upgrade you can make to your small business website.",
  },
];

export default function BlogIndex() {
  return (
    <>
      <Helmet>
        <title>Blog — AI Chatbot Tips for Small Businesses | Wispy AI</title>
        <meta
          name="description"
          content="Practical guides and tips for small business owners on using AI chatbots to capture leads, answer questions, and grow revenue."
        />
        <meta property="og:title" content="Blog — Wispy AI" />
        <meta
          property="og:description"
          content="Practical guides and tips for small business owners on using AI chatbots."
        />
        <meta property="og:url" content="https://wispyaii.com/blog" />
      </Helmet>

      <style>{styles}</style>

      <div className="bl-root">
        <header className="bl-hero">
          <div className="bl-tag">Resources</div>
          <h1 className="bl-hero-title">The Wispy AI Blog</h1>
          <p className="bl-hero-sub">
            Practical guides for small business owners on growing with AI.
          </p>
        </header>

        <main className="bl-body">
          <div className="bl-nav">
            <Link to="/" className="bl-nav-home">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Wispy AI
            </Link>
            <span className="bl-count">{posts.length} articles</span>
          </div>

          <div className="bl-grid">
            {posts.map((post) => (
              <Link key={post.slug} to={post.slug} className="bl-card">
                <div className="bl-card-meta">
                  <span className="bl-card-tag">{post.tag}</span>
                  <span className="bl-card-date">{post.date}</span>
                  <span className="bl-card-read">{post.read}</span>
                </div>
                <h2 className="bl-card-title">{post.title}</h2>
                <p className="bl-card-desc">{post.desc}</p>
                <span className="bl-card-link">
                  Read article
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="#1D9E75" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
